import { useMemo } from 'react'
import { isToday, isYesterday } from 'date-fns'
import { i18n, formatDate, formatNumber } from '@/locale/i18n'
import Currency from '@/components/currency/Currency'
import constants from '@/constants'
import { getPositionLabels } from '@/features/market/utils/positionLabels'
import {
  WAGER_ACTION,
  isWagerSaleLikeAction,
  normalizeWagerAction,
} from '@/constants/marketBackendEnums'

const POSITIVE = 'var(--color-positive-4)'
const NEGATIVE = 'var(--color-negative-4)'
const MUTED = 'var(--color-Neutral-4)'
// Placing a bet and closing one are the two everyday events, so they get their own hues
// instead of competing with the green/red that mark how a market actually resolved.
const BET = 'var(--color-informative-4)'
const SOLD = 'var(--color-warning-4)'

const isFeeAction = action => action === 'fee' || action === 'trading_fee'

/** Word and colour for one entry, keyed by what happened. Shared by the label and the dot. */
const describeAction = action => {
  if (isFeeAction(action)) return { label: i18n('transaction_card.fees'), color: MUTED }

  const normalized = normalizeWagerAction(action)
  if (isWagerSaleLikeAction(normalized)) return { label: i18n('transaction_card.sold'), color: SOLD }

  switch (normalized) {
    case WAGER_ACTION.PURCHASE:
      return { label: i18n('transaction_card.open'), color: BET }
    case WAGER_ACTION.WIN:
      return { label: i18n('transaction_card.won'), color: POSITIVE }
    case WAGER_ACTION.LOSE:
    case WAGER_ACTION.DISABLE:
      return { label: i18n('transaction_card.lost'), color: NEGATIVE }
    case WAGER_ACTION.CANCEL:
      return { label: i18n('transaction_card.canceled'), color: MUTED }
    case WAGER_ACTION.REVERSED:
      return { label: i18n('transaction_card.reversed'), color: MUTED }
    default:
      return { label: null, color: MUTED }
  }
}

const dayLabel = date => {
  if (isToday(date)) return i18n('positions_timeline.today')
  if (isYesterday(date)) return i18n('positions_timeline.yesterday')
  return formatDate(date, 'MMM dd, yyyy')
}

/**
 * Desktop lays the fields out as aligned columns so they can be scanned down the list.
 * Every column is capped and a trailing track absorbs the leftover width, so the fields
 * stay packed to the left instead of drifting apart across a wide page. Below `md` the
 * same fields fold into two compact lines.
 */
const GRID_COLUMNS =
  'md:grid-cols-[124px_96px_minmax(0,220px)_minmax(0,200px)_minmax(0,120px)_minmax(0,1fr)] md:gap-x-6'

/** Exported so the loading skeleton lays its rows out on the very same tracks. */
export const ENTRY_LAYOUT = `flex flex-col gap-1 md:grid ${GRID_COLUMNS} md:items-baseline md:gap-y-0`

/** The rail sits at the dot's centre so it never crosses the entries' text column. */
const RAIL_LEFT = '5px'

const TransactionsTimeline = ({ transactions = [], question }) => {
  const days = useMemo(() => {
    const grouped = new Map()

    transactions.forEach(transaction => {
      const date = new Date(transaction.created)
      const key = formatDate(date, 'yyyy-MM-dd')
      if (!grouped.has(key)) grouped.set(key, { key, date, entries: [] })
      grouped.get(key).entries.push(transaction)
    })

    return Array.from(grouped.values())
  }, [transactions])

  const renderOutcome = transaction => {
    const { longLabel, shortLabel } = getPositionLabels(transaction.outcome, question)
    const positionLabel = transaction.position === constants.position.LONG ? longLabel : shortLabel
    const positionColor = transaction.position === constants.position.LONG ? POSITIVE : NEGATIVE
    const outcomesCount = question?.outcomes_count ?? question?.outcomes?.length ?? 0

    if (outcomesCount > 1 && transaction.outcome?.title) {
      return (
        <>
          <span style={{ color: positionColor }}>{positionLabel}</span> · {transaction.outcome.title}
        </>
      )
    }

    return <span style={{ color: positionColor }}>{positionLabel}</span>
  }

  const renderSize = transaction => {
    if (isFeeAction(transaction.action)) return null

    const parts = []
    if (transaction.shares) parts.push(`${formatNumber(transaction.shares)} ${i18n('bet.shares').toLowerCase()}`)
    if (transaction.price) {
      parts.push(
        `${i18n('positions_timeline.at')} ${formatNumber(transaction.price, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`
      )
    }
    return parts.join(' ')
  }

  const renderProfit = transaction => {
    const hasProfit =
      transaction.profit !== undefined &&
      transaction.profit !== null &&
      normalizeWagerAction(transaction.action) === WAGER_ACTION.PURCHASE
    if (!hasProfit) return null

    const isProfit = transaction.profit > 0
    return (
      <span className="positions-timeline__profit text-sm" style={{ color: isProfit ? POSITIVE : NEGATIVE }}>
        {isProfit ? '+' : ''}
        {formatNumber(transaction.profit, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%
      </span>
    )
  }

  const renderRebate = transaction => {
    const rebate = Number(transaction.maker_rebate ?? 0)
    if (rebate === 0) return null

    return (
      <span className="positions-timeline__rebate text-sm font-semibold flex items-center gap-1 text-brand-gradient">
        <span>{i18n('positions_timeline.rebate')}:</span>
        <Currency
          value={rebate}
          currency={transaction.currency}
          className="text-sm font-semibold !inline-flex"
          valueClassName=" "
          roundingMode="halfCeil"
          noNumberFlow
        />
      </span>
    )
  }

  return (
    <div className="positions-timeline relative">
      {/* One continuous rail behind the whole list, day headings included. */}
      <span
        aria-hidden="true"
        className="positions-timeline__rail absolute top-3 bottom-3 w-px bg-Neutral-3"
        style={{ left: RAIL_LEFT }}
      />

      {days.map((day, dayIndex) => (
        <section key={day.key} className="positions-timeline__day">
          {/* Sticky only where the list has its own scroller (see `.bet-list__scroll`);
              on phones it flows with the page. */}
          <h5
            className={`positions-timeline__day-label md:sticky md:top-0 z-[1] w-full pl-9 pb-1 bg-surface-1 text-xs font-bold uppercase tracking-wide text-Neutral-6 whitespace-nowrap ${
              dayIndex === 0 ? 'pt-4' : 'pt-7'
            }`}>
            {dayLabel(day.date)}
          </h5>

          <ol className="positions-timeline__entries m-0 p-0 list-none">
            {day.entries.map(transaction => {
              const { label, color } = describeAction(transaction.action)
              const size = renderSize(transaction)
              const isFee = isFeeAction(transaction.action)

              return (
                <li key={`timeline-${transaction.id}`} className="positions-timeline__entry relative pl-9 py-3">
                  {/* The ring punches a gap in the rail so each event reads as a stop on it. */}
                  <span
                    aria-hidden="true"
                    className="positions-timeline__dot absolute left-0 top-[22px] h-[11px] w-[11px] rounded-full ring-4 ring-surface-1"
                    style={{ backgroundColor: color }}
                  />

                  <div className={`positions-timeline__entry-grid ${ENTRY_LAYOUT}`}>
                    {/* One line on mobile, two columns on desktop. */}
                    <div className="positions-timeline__when flex items-center gap-3 md:contents">
                      <time
                        className="positions-timeline__time text-sm text-Neutral-4"
                        dateTime={transaction.created}>
                        {formatDate(transaction.created, 'HH:mm')}
                      </time>
                      {/* Always rendered: the wrappers are `md:contents`, so dropping this
                          node would shift every column after it up a track. It collapses
                          only on mobile, where the fields are stacked instead of aligned. */}
                      <span
                        className="positions-timeline__action font-semibold empty:hidden md:empty:block"
                        style={{ color }}>
                        {label}
                      </span>
                    </div>

                    <div className="positions-timeline__outcome min-w-0 truncate">
                      {!isFee && renderOutcome(transaction)}
                    </div>

                    <div className="positions-timeline__figures flex items-baseline justify-between gap-4 md:contents">
                      <span className="positions-timeline__size text-sm text-Neutral-4 truncate md:text-left">
                        {size}
                      </span>
                      <div className="positions-timeline__amount flex flex-col items-end md:items-start">
                        <span className="positions-timeline__amount-value font-semibold whitespace-nowrap">
                          <Currency value={transaction.amount} currency={transaction.currency} noNumberFlow />
                        </span>
                        {renderProfit(transaction)}
                        {renderRebate(transaction)}
                      </div>
                    </div>
                  </div>
                </li>
              )
            })}
          </ol>
        </section>
      ))}
    </div>
  )
}

export default TransactionsTimeline
